/**
 * Commission formulas (design spec §7). All amounts are integer Rial
 * (`bigint`), all rates are basis points (1% = 100). Every product is
 * floored to the Rial independently.
 */

import { assertBp, bpOf, type Rial } from './money';

const FULL_BP = 10000;

function assertNonNegative(value: bigint, name: string): void {
  if (value < 0n) throw new RangeError(`${name} must be non-negative, got ${value}`);
}

export interface CodeSplitInput {
  /** Eligible list price L (Rial, before discount, without tax). */
  L: Rial;
  /** Code pool rate r (bp). */
  r_bp: number;
  /** Discount d chosen by the affiliate for the customer (bp). */
  d_bp: number;
  /** Maximum discount the affiliate may give (bp). */
  d_max_bp: number;
}

export interface CodeSplit {
  /** P = L × (1 − d) */
  customerPays: Rial;
  /** C = L × (r − d) */
  affiliateGets: Rial;
  /** L × r, independent of d */
  mizbanfaCost: Rial;
  /** L × d */
  discount: Rial;
}

/**
 * Split of an affiliate discount code (spec §7.3), with `0 ≤ d ≤ d_max ≤ r ≤ 100%`.
 *
 * Each value is floored independently, so in rare cases
 * `customerPays + discount` or `affiliateGets + discount` can be 1 Rial below
 * `L` or `mizbanfaCost` respectively.
 *
 * Throws `RangeError` if d < 0, d > d_max, d_max > r, r > 100%, or L < 0.
 */
export function computeCodeSplit(input: CodeSplitInput): CodeSplit {
  const { L, r_bp, d_bp, d_max_bp } = input;
  assertNonNegative(L, 'L');
  assertBp(r_bp, 'r_bp', FULL_BP);
  assertBp(d_max_bp, 'd_max_bp');
  assertBp(d_bp, 'd_bp');
  if (d_max_bp > r_bp) throw new RangeError(`d_max (${d_max_bp}) must not exceed r (${r_bp})`);
  if (d_bp > d_max_bp) throw new RangeError(`d (${d_bp}) must not exceed d_max (${d_max_bp})`);
  return {
    customerPays: bpOf(L, FULL_BP - d_bp),
    affiliateGets: bpOf(L, r_bp - d_bp),
    mizbanfaCost: bpOf(L, r_bp),
    discount: bpOf(L, d_bp),
  };
}

/** Commission for a link (or refcode / campaign) sale: floor(L × r). */
export function computeLinkCommission(L: Rial, r_bp: number): Rial {
  assertNonNegative(L, 'L');
  assertBp(r_bp, 'r_bp', FULL_BP);
  return bpOf(L, r_bp);
}

export interface MultiYearInput {
  /** Eligible amount for the whole prepaid period (Rial). */
  amount: Rial;
  /** Length of the prepaid period in months (integer ≥ 1). */
  months: number;
  /** Rate for the first 12 months (bp). */
  newRateBp: number;
  /** Rate for the months after the first 12 (bp). */
  renewalRateBp: number;
}

export interface MultiYearSplit {
  /** Part of `amount` covering the first 12 months. */
  newBase: Rial;
  /** Part of `amount` covering the remaining months (`amount − newBase`). */
  renewalBase: Rial;
  newCommission: Rial;
  renewalCommission: Rial;
  /** `newCommission + renewalCommission` */
  total: Rial;
}

/**
 * Multi-year prepayment (spec §7.1): the first 12 months earn the `new`
 * rate, the rest the `renewal` rate, split proportionally by months.
 * `newBase = floor(amount × min(months, 12) / months)` and
 * `renewalBase = amount − newBase`, so the bases always sum to `amount`.
 */
export function splitMultiYear(input: MultiYearInput): MultiYearSplit {
  const { amount, months, newRateBp, renewalRateBp } = input;
  assertNonNegative(amount, 'amount');
  if (!Number.isSafeInteger(months) || months < 1) {
    throw new RangeError(`months must be a positive integer, got ${months}`);
  }
  assertBp(newRateBp, 'newRateBp', FULL_BP);
  assertBp(renewalRateBp, 'renewalRateBp', FULL_BP);
  const newMonths = BigInt(Math.min(months, 12));
  const newBase = (amount * newMonths) / BigInt(months);
  const renewalBase = amount - newBase;
  const newCommission = bpOf(newBase, newRateBp);
  const renewalCommission = bpOf(renewalBase, renewalRateBp);
  return { newBase, renewalBase, newCommission, renewalCommission, total: newCommission + renewalCommission };
}

/**
 * Clawback after a refund of an approved commission (spec §7.5):
 * `floor(commission × refundAmount / invoiceTotal)`.
 *
 * Returns the positive amount to claw back. A refund larger than the invoice
 * total is capped at the total (full clawback). Throws if `invoiceTotal ≤ 0`
 * or any amount is negative.
 */
export function proportionalClawback(commission: Rial, refundAmount: Rial, invoiceTotal: Rial): Rial {
  assertNonNegative(commission, 'commission');
  assertNonNegative(refundAmount, 'refundAmount');
  if (invoiceTotal <= 0n) throw new RangeError(`invoiceTotal must be positive, got ${invoiceTotal}`);
  const refund = refundAmount > invoiceTotal ? invoiceTotal : refundAmount;
  return (commission * refund) / invoiceTotal;
}

/** Convert a percentage given as basis points to a WHMCS percent decimal string: 800 -> "8.00". */
export function bpToPercentString(bp: number): string {
  assertBp(bp, 'bp');
  const big = BigInt(bp);
  const frac = (big % 100n).toString().padStart(2, '0');
  return `${big / 100n}.${frac}`;
}
